/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

import { useTranslation } from "@iam/design-system/i18n";
import { Button } from "@iam/design-system/primitives";
import { Play, Square } from "lucide-react";
import { useState } from "react";
import type { SessionMode } from "../lib/types";

export type SessionRunnerProps = {
  running: boolean;
  onRun: (goal: string, mode: SessionMode) => void;
  onCancel: () => void;
  disabled?: boolean;
  "data-testid"?: string;
};

const MODES: SessionMode[] = ["auto", "confirm", "plan", "research", "chat"];

/**
 * SessionRunner is the agent-session launch form: a goal textarea, a mode
 * selector, and run / cancel actions. It is a controlled-output component —
 * it holds only its own draft state and delegates the run to the store.
 */
export function SessionRunner({
  running,
  onRun,
  onCancel,
  disabled = false,
  "data-testid": testId,
}: SessionRunnerProps) {
  const t = useTranslation();
  const [goal, setGoal] = useState("");
  const [mode, setMode] = useState<SessionMode>("auto");

  /** Validate draft and invoke onRun, or no-op when empty/running. */
  const submit = () => {
    if (goal.trim() === "" || running) return;
    onRun(goal, mode);
  };

  return (
    <form
      className="console-runner"
      data-testid={testId}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="console-runner__label" htmlFor="console-goal">
        {t("session.goal")}
      </label>
      <textarea
        id="console-goal"
        className="console-runner__goal"
        value={goal}
        disabled={disabled || running}
        placeholder={t("session.goal.placeholder")}
        onChange={(e) => setGoal(e.target.value)}
        rows={3}
      />
      <div className="console-runner__controls">
        <label className="console-runner__mode">
          <span className="console-runner__mode-label">{t("session.mode")}</span>
          <select
            value={mode}
            disabled={disabled || running}
            onChange={(e) => setMode(e.target.value as SessionMode)}
            aria-label={t("session.mode")}
          >
            {MODES.map((m) => (
              <option key={m} value={m}>
                {t(`session.mode.${m}`)}
              </option>
            ))}
          </select>
        </label>
        <div className="console-runner__actions">
          {running && (
            <Button type="button" variant="ghost" iconLeft={<Square />} onClick={onCancel}>
              {t("session.cancel")}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            iconLeft={<Play />}
            loading={running}
            disabled={disabled || goal.trim() === ""}
          >
            {running ? t("session.running") : t("session.run")}
          </Button>
        </div>
      </div>
    </form>
  );
}
